import { useEffect, useState } from "react";
import { getAdminAnalytics } from "@/lib/api";

const RANGES = [7, 30, 90];

const fmtDay = (d) => new Date(`${d}T00:00:00`).toLocaleDateString("en-AU", { day: "numeric", month: "short" });

const Stat = ({ label, value, hint, testId }) => (
  <div className="border border-deep-sea/15 bg-cream/40 p-5" data-testid={testId}>
    <p className="eyebrow">{label}</p>
    <p className="font-serif text-deep-sea text-4xl mt-2 leading-none" data-testid={`${testId}-value`}>{value.toLocaleString("en-AU")}</p>
    {hint && <p className="text-ink/55 text-xs mt-2">{hint}</p>}
  </div>
);

const Bar = ({ value, max, className }) => (
  <div className="h-1.5 bg-deep-sea/10 w-full overflow-hidden">
    <div className={`h-full ${className}`} style={{ width: `${max ? Math.max(2, (value / max) * 100) : 0}%` }} />
  </div>
);

const DailyChart = ({ daily }) => {
  const maxViews = Math.max(1, ...daily.map((d) => d.views));
  const maxReg = Math.max(1, ...daily.map((d) => d.registrations + d.enquiries));
  const tick = Math.max(1, Math.round(daily.length / 6));
  return (
    <div data-testid="admin-traffic-daily">
      <div className="flex items-center gap-5 text-xs text-ink/60 mb-3">
        <span className="inline-flex items-center gap-2"><span className="w-3 h-3 bg-deep-sea inline-block" /> Page views</span>
        <span className="inline-flex items-center gap-2"><span className="w-3 h-3 bg-gold inline-block" /> Registrations</span>
        <span className="inline-flex items-center gap-2"><span className="w-3 h-3 bg-gold/40 inline-block" /> Enquiries</span>
      </div>
      <div className="flex items-end gap-[2px] h-40 border-b border-deep-sea/20">
        {daily.map((d) => (
          <div key={d.day} className="flex-1 flex items-end justify-center gap-[1px] h-full group relative" title={`${fmtDay(d.day)} — ${d.views} views · ${d.visitors} visitors · ${d.registrations} registrations · ${d.enquiries} enquiries`}>
            <div className="w-1/2 bg-deep-sea/80 group-hover:bg-deep-sea transition-colors" style={{ height: `${(d.views / maxViews) * 100}%` }} />
            <div className="w-1/2 flex flex-col justify-end" style={{ height: "100%" }}>
              <div className="bg-gold/40" style={{ height: `${(d.enquiries / maxReg) * 100}%` }} />
              <div className="bg-gold" style={{ height: `${(d.registrations / maxReg) * 100}%` }} />
            </div>
          </div>
        ))}
      </div>
      <div className="flex justify-between text-[10px] text-ink/50 mt-1.5">
        {daily.filter((_, i) => i % tick === 0 || i === daily.length - 1).map((d) => <span key={d.day}>{fmtDay(d.day)}</span>)}
      </div>
    </div>
  );
};

const AdminTraffic = ({ token, onUnauthorized }) => {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let live = true;
    setLoading(true);
    getAdminAnalytics(token, days)
      .then((d) => live && setData(d))
      .catch((err) => { if (err.response?.status === 401) onUnauthorized(); })
      .finally(() => live && setLoading(false));
    return () => { live = false; };
  }, [days, token, onUnauthorized]);

  const t = data?.totals || { views: 0, visitors: 0, registrations: 0, enquiries: 0, hair_enquiries: 0, bookings: 0 };
  const maxPage = data?.pages?.[0]?.views || 0;
  const maxSource = data?.sources?.[0]?.views || 0;

  return (
    <div className="mt-8" data-testid="admin-traffic">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex gap-2" data-testid="admin-traffic-range">
          {RANGES.map((r) => (
            <button key={r} onClick={() => setDays(r)} data-testid={`admin-traffic-range-${r}`}
              className={`rounded-full border px-4 py-2 text-xs tracking-[0.12em] uppercase font-medium transition-colors duration-300 ${
                days === r ? "bg-deep-sea text-cream border-deep-sea" : "border-deep-sea/20 text-deep-sea hover:border-gold"
              }`}>
              Last {r} days
            </button>
          ))}
        </div>
        <p className="text-ink/50 text-xs">Counted on this website, anonymously (no cookies) · Perth time{loading ? " · updating…" : ""}</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6" data-testid="admin-traffic-stats">
        <Stat label="Page views" value={t.views} testId="admin-stat-views" />
        <Stat label="Visitors" value={t.visitors} hint="Unique browsing sessions" testId="admin-stat-visitors" />
        <Stat label="Registrations" value={t.registrations} hint="Lumière list" testId="admin-stat-registrations" />
        <Stat label="Enquiries" value={t.enquiries} hint={`${t.hair_enquiries.toLocaleString("en-AU")} hair`} testId="admin-stat-enquiries" />
      </div>

      <section className="mt-10">
        <h2 className="font-serif text-deep-sea text-2xl">Day by day</h2>
        <div className="mt-5">{data && <DailyChart daily={data.daily} />}</div>
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 mt-12">
        <section className="lg:col-span-2" data-testid="admin-traffic-pages">
          <h2 className="font-serif text-deep-sea text-2xl">Traffic by page</h2>
          {data && data.pages.length === 0 && <p className="text-ink/60 text-sm mt-4" data-testid="admin-traffic-empty">No page views recorded in this period yet.</p>}
          <table className="w-full mt-5 text-sm">
            <thead>
              <tr className="text-left text-[11px] tracking-[0.14em] uppercase text-ink/55 border-b border-deep-sea/15">
                <th className="py-2 font-medium">Page</th>
                <th className="py-2 font-medium text-right w-20">Views</th>
                <th className="py-2 font-medium text-right w-24">Visitors</th>
              </tr>
            </thead>
            <tbody>
              {data?.pages.map((p) => (
                <tr key={p.path} className="border-b border-deep-sea/10 align-top" data-testid={`admin-page-row-${p.path.replace(/\//g, "-") || "root"}`}>
                  <td className="py-3 pr-4">
                    <a href={p.path} target="_blank" rel="noreferrer" className="text-deep-sea hover:text-gold font-medium break-all">{p.path}</a>
                    <div className="mt-2"><Bar value={p.views} max={maxPage} className="bg-deep-sea/70" /></div>
                  </td>
                  <td className="py-3 text-right text-ink/85 tabular-nums">{p.views.toLocaleString("en-AU")}</td>
                  <td className="py-3 text-right text-ink/85 tabular-nums">{p.visitors.toLocaleString("en-AU")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        <section data-testid="admin-traffic-sources">
          <h2 className="font-serif text-deep-sea text-2xl">Where visitors came from</h2>
          <ul className="mt-5 space-y-4">
            {data?.sources.map((s) => (
              <li key={s.source}>
                <div className="flex justify-between text-sm">
                  <span className="text-deep-sea font-medium">{s.source}</span>
                  <span className="text-ink/70 tabular-nums">{s.views.toLocaleString("en-AU")} <span className="text-ink/45">· {s.visitors.toLocaleString("en-AU")} visitors</span></span>
                </div>
                <div className="mt-1.5"><Bar value={s.views} max={maxSource} className="bg-gold" /></div>
              </li>
            ))}
          </ul>
          {data && data.sources.length === 0 && <p className="text-ink/60 text-sm mt-4">Nothing yet.</p>}
        </section>
      </div>
    </div>
  );
};

export default AdminTraffic;
